import { Button, Checkbox, LoadingOverlay, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconFileExport, IconFileImport } from "@tabler/icons-react"
import { motion } from "framer-motion"
import { Package } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import { Sale } from "../../../models/sales.model"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import { onViewFile } from "../../../shared/utility/view-file"
import StatCard from "../../dashboard/component/stat-cart"
import { exportSales } from "../api/sale.api"
import ExportSalesModalComponent from "../component/export-sales-modal.component"


import FileViewerModal from "../../../shared/component/file-viewer/file-viewer-modal.component"
import { notification } from "../../../shared/component/notification/utility/notification"
import { useLazyCountParkingLotsQuery, useLazyGetParkingLotsQuery } from "../store/parking-lot.query"
import { ParkingLot } from "../../../models/parking-lot.model"

export default function ParkingPage() {
  const params = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [openedImport, { open: openImport, close: closeImport }] =
    useDisclosure()
  const [fileOpened, { open: fileOpen, close: fileClose }] = useDisclosure()
  const [selectedFiles, setSelectedFiles] = useState<string[]>([])

  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: ["property"],
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })

  const [getParkingLots, parkingLots] = useLazyGetParkingLotsQuery()
  const [countParkingLots, totalParkingLots] = useLazyCountParkingLotsQuery()
  const [countActiveParkingLots, activeParkingLots] = useLazyCountParkingLotsQuery()
  const [countInactiveParkingLots, inactiveParkingLots] = useLazyCountParkingLotsQuery()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  // const [removeSalesFile, removeSalesFileResponse] =
  //   useRemoveSalesFileMutation()
  const generateFilterOption = (value: boolean): Filter[][] => {
    if (userInfo.role.key == "super_admin") {
      // return [[{ field: "enabled", value: value as boolean, operator: "=" }]]
    }
    return [
      // [{ field: "enabled", value: value as boolean, operator: "=" }],
      // [{ field: "ownerId", value: ownerInfo?.parentOwnerId, operator: "=" }],
    ]
  }
  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getParkingLots({
        ...collection,
        // filter: [
        //   [{ field: "owner.confidential", value: false, operator: "=" }],
        // ],
      })
    } else {
      getParkingLots({
        ...collection,
        filter: [
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [collection])
  useEffect(() => {
    if (userInfo.role.key == "super_admin") {
      countParkingLots({
        count: true,
      })
    } else {
      countParkingLots({
        count: true,
        includes: ["property"],
        filter: [
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
    countActiveParkingLots({
      count: true,
      filter: generateFilterOption(true),
    })
    countInactiveParkingLots({
      count: true,
      filter: generateFilterOption(false),
    })
  }, [])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/tenant/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  const data: ParkingLot[] | undefined = parkingLots.data?.data

  const config: EntityConfig<ParkingLot> = {
    primaryColumn: {
      key: "name",
      name: t("name"),
      render: (parkingLot: ParkingLot) => {
        return (
          <Text size="sm" className={parkingLot?.deletedAt ? "text-danger" : ""}>
            {parkingLot?.name}
          </Text>
        )
      },
    },
    detailUrl: "detail",
    rootUrl: "/parking-lot",
    identity: "id",
    name: "name",
    showDetail: true,
    visibleColumn: [
       {
        key: "property",
        name: t("property", { count: 1 }),
        render(value) {
          return <>{value?.property?.name}</>
        },
      },
      {
        key: "name",
        name: t("name"),
        render: (parkingLot: ParkingLot) => {
          return (
            <Text size="sm" className={parkingLot?.deletedAt ? "text-danger" : ""}>
              {parkingLot?.name}
            </Text>
          )
        },
      },
      { key: "totalSlots", name: t("totalSlots") },
      { key: "availableSlots", name: t("availableSlots") },
     
      {
        key: "createdAt",
        name: t("createdAt"),
        isDate: true,
        render: (value: ParkingLot) => {
          return <Text size="sm">{dateFormat(value?.createdAt)}</Text>
        },
      },
      {
        key: "status",
        name: t("status"),
        isBoolean: true,
        // render: (value: ParkingLot) => {
        //   return <Text size="sm">{dateFormat(value)}</Text>
        // },
      },
    ],
  }

 
  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={parkingLots.isFetching} />

      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={t("totalParkingLots")}
          icon={Package}
          value={totalParkingLots?.data?.count}
          color="#6366F1"
        />
        {/* <StatCard
          name={t("activeTenants")}
          icon={TrendingUp}
          value={activeSales?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={t("inactiveTenants")}
          icon={AlertTriangle}
          value={inactiveSales?.data?.count}
          color="#EC4899"
        /> */}
        {/* <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' /> */}
      </motion.div>
      <EntityList
        viewMode={viewMode}
        title={t("parkingLot", { count: 2 })}
        showExport={false}
        // header={
        //   <>
        //     <div className="h-14 dark:border-gray-500 dark:bg-gray-500 flex items-center justify-between p-2 border-0 border-b">
        //       <div className="h-full flex items-center space-x-4 font-semibold dark:text-white">
        //         {t("sale", { count: 2 })}
        //       </div>
        //       {
        //         <div className="h-full  space-x-4 flex items-center">
        //           {viewMode == "list" && (
        //             <>
        //               <Checkbox
        //                 size="sm"
        //                 onChange={(e) =>
        //                   setCollection({
        //                     ...collection,
        //                     withArchived: e.currentTarget.checked
        //                       ? true
        //                       : false,
        //                   })
        //                 }
        //                 label={
        //                   <span className="text-xs text-gray-500 dark:text-gray-400">
        //                     {t("showArchived")}
        //                   </span>
        //                 }
        //               />

        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openExport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileExport />
        //                 </span>
        //                 <span>{t("export")}</span>
        //               </Button>
        //               <Button
        //                 classNames={{ label: "flex space-x-2 text-white" }}
        //                 className="bg-primary dark:bg-dark_primary"
        //                 size="xs"
        //                 type="button"
        //                 onClick={(e) => openImport()}
        //                 variant="filled"
        //               >
        //                 <span className="text-white">
        //                   <IconFileImport />
        //                 </span>
        //                 <span>{t("import")}</span>
        //               </Button>
        //             </>
        //           )}
        //           <Link to={`/tenant/new`} state={{ id: location?.state?.id }}>
        //             <Button
        //               classNames={{ label: "flex space-x-2 text-white" }}
        //               className="bg-primary dark:bg-dark_primary"
        //               size="xs"
        //               variant="filled"
        //             >
        //               <span className="text-white">
        //                 <svg
        //                   xmlns="http://www.w3.org/2000/svg"
        //                   className="fill-current h-4 text-white"
        //                   viewBox="0 0 24 24"
        //                 >
        //                   <path
        //                     fillRule="evenodd"
        //                     d="M11 2L11 11L2 11L2 13L11 13L11 22L13 22L13 13L22 13L22 11L13 11L13 2Z"
        //                   />
        //                 </svg>
        //               </span>
        //               <span>{t("new")}</span>
        //             </Button>
        //           </Link>
        //         </div>
        //       }
        //     </div>
        //   </>
        // }
        total={parkingLots?.data?.count}
        itemsLoading={parkingLots.isFetching}
        config={config}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onExport={openExport}
        
      />

      <ExportSalesModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"sales.xlsx"}
        // filteringVariables={[
        //   [
        //     {
        //       field: "enabled",
        //       value: true,
        //       name: t("active"),
        //       operator: "=",
        //     },
        //     {
        //       field: "enabled",
        //       value: false,
        //       name: t("inactive"),
        //       operator: "=",
        //     },
        //   ],
        //   [
        //     {
        //       field: "gender",
        //       value: "male",
        //       name: t("male"),
        //       operator: "=",
        //     },
        //     {
        //       field: "gender",
        //       value: "female",
        //       name: t("female"),
        //       operator: "=",
        //     },
        //   ],
        // ]}
        exportFunction={exportSales}
      />

      {/* <FileViewerModal
        open={fileOpen}
        close={fileClose}
        fileType={t("sale", { count: 1 })}
        files={selectedFiles as string[]}
        opened={fileOpened}
        remove={handleFileRemoval}
        removeLoading={removeSalesFileResponse?.isLoading}
        fileNames={[]}
      /> */}
    </main>
  )
}
